import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { HouseMark } from '@/components/generals/house-mark'
import { MEDIA } from '@/constants/media'

/* Web design ---------------------------------------------------------------- */

interface BrowserFrameProps {
    src: string
    alt: string
    address: string
    className?: string
}

const BrowserFrame: FC<BrowserFrameProps> = ({ src, alt, address, className }): ReactNode => {
    return (
        <figure className={cn('absolute flex flex-col overflow-hidden border border-paper/15 bg-ink', className)}>
            <div className='flex h-8 shrink-0 items-center justify-between border-b border-paper/10 px-3.5'>
                <span aria-hidden='true' className='flex gap-1.5'>
                    <span className='size-1.5 bg-paper/35' />
                    <span className='size-1.5 bg-paper/20' />
                    <span className='size-1.5 bg-paper/10' />
                </span>

                <span className='label text-[0.58rem] text-haze'>{address}</span>
                <span aria-hidden='true' className='w-6' />
            </div>

            <div data-frame='' className='relative min-h-0 flex-1 overflow-hidden'>
                <img
                    data-shot=''
                    src={src}
                    alt={alt}
                    loading='lazy'
                    decoding='async'
                    className='absolute inset-0 size-full object-cover object-top'
                />
            </div>
        </figure>
    )
}

/** Two live-looking pages that scroll while the chapter is read. */
export const WebVisual: FC = (): ReactNode => {
    return (
        <div data-visual='web' className='relative mx-auto aspect-[6/5] w-full max-w-[40rem]'>
            <BrowserFrame
                src={MEDIA.previewBack.src}
                alt={MEDIA.previewBack.alt}
                address="Interior Design Firm"
                className='left-0 top-0 h-[78%] w-[70%]'
            />

            <BrowserFrame
                src={MEDIA.previewFront.src}
                alt={MEDIA.previewFront.alt}
                address="Steak House"
                className='bottom-0 right-0 h-[74%] w-[62%] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]'
            />

            <div data-badge='' className='absolute -left-2 bottom-[4%] bg-signal px-5 py-4 text-ink md:-left-6'>
                <p className='display text-[clamp(2rem,3.4vw,3.2rem)] leading-none wdth-75'>2x–3x</p>
                <p className='label mt-2 text-[0.62rem]'>higher conversion rate</p>
            </div>
        </div>
    )
}

/* SEO ---------------------------------------------------------------------- */

const COMPETITORS = [0.86, 0.7, 0.92, 0.62, 0.78, 0.55]

/** A results list where your business climbs from seventh place to first as the chapter scrolls. */
export const RankVisual: FC = (): ReactNode => {
    return (
        <div data-visual='rank' className='relative mx-auto w-full max-w-[34rem]'>
            <div className='label flex items-center justify-between text-frost'>
                <span>Local search results</span>
                <span>Your service area</span>
            </div>

            <div className='relative mt-6 grid grid-cols-[2.5rem_1fr]' aria-hidden='true'>
                <ol className='label text-haze'>
                    {Array.from({ length: 7 }, (_, index) => (
                        <li key={index} className='flex h-14 items-center'>
                            {String(index + 1).padStart(2, '0')}
                        </li>
                    ))}
                </ol>

                <div className='relative'>
                    {COMPETITORS.map((width, index) => (
                        <div
                            key={width}
                            data-rank-row=''
                            className='absolute inset-x-0 flex h-14 items-center gap-3 border-t border-paper/10'
                            style={{ top: `${index * 3.5}rem` }}
                        >
                            <span className='h-2 bg-paper/25' style={{ width: `${width * 70}%` }} />
                            <span className='h-2 w-8 bg-paper/10' />
                        </div>
                    ))}

                    <div
                        data-rank-you=''
                        className='absolute inset-x-0 flex h-14 items-center justify-between bg-signal px-4 text-ink'
                        style={{ top: `${6 * 3.5}rem` }}
                    >
                        <span className='text-sm font-semibold md:text-base'>Your business</span>
                        <HouseMark className='size-6' houseClassName='fill-ink stroke-ink' checkClassName='stroke-signal' />
                    </div>
                </div>
            </div>

            <p className='mt-8 flex items-baseline gap-4'>
                <span className='display text-[clamp(2.6rem,4.6vw,4.4rem)] leading-none text-signal wdth-75'>500%</span>
                <span className='label max-w-[16ch] text-frost'>Up to 500% increase in traffic</span>
            </p>
        </div>
    )
}

/* Print -------------------------------------------------------------------- */

const SWATCHES = [
    { name: "Ink", hex: '#151E21', className: 'bg-ink' },
    { name: "Cyan", hex: '#0CB7EB', className: 'bg-signal' },
    { name: "Frost", hex: '#C3EFFD', className: 'bg-frost' }
]

const CropMarks: FC = (): ReactNode => {
    const corner = 'absolute size-5 border-ink/70'

    return (
        <span data-crop='' aria-hidden='true' className='pointer-events-none absolute -inset-5'>
            <span className={cn(corner, 'left-0 top-0 border-r border-b')} />
            <span className={cn(corner, 'right-0 top-0 border-b border-l')} />
            <span className={cn(corner, 'bottom-0 left-0 border-r border-t')} />
            <span className={cn(corner, 'bottom-0 right-0 border-l border-t')} />
        </span>
    )
}

const RegistrationMark: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <svg data-reg='' viewBox='0 0 40 40' aria-hidden='true' className={cn('size-7 text-ink', className)}>
            <circle cx='20' cy='20' r='9' fill='none' stroke='currentColor' strokeWidth='1.5' />
            <circle cx='20' cy='20' r='4.5' fill='currentColor' />
            <path d='M20 2V38M2 20H38' stroke='currentColor' strokeWidth='1.5' />
        </svg>
    )
}

/** A tri-fold brochure that opens flat on scroll, framed by printer's marks and the brand's ink set. */
export const PrintVisual: FC = (): ReactNode => {
    return (
        <div data-visual='print' className='relative mx-auto w-full max-w-[38rem] px-6 pb-20 pt-12'>
            <RegistrationMark className='absolute left-1/2 top-0 -translate-x-1/2' />

            <div aria-hidden='true' className='relative [perspective:2200px]'>
                <CropMarks />

                <div className='relative grid aspect-[3/2] grid-cols-3 [transform-style:preserve-3d]'>
                    <div data-fold='left' className='relative origin-right [transform-style:preserve-3d]'>
                        <div className='absolute inset-0 flex flex-col justify-between bg-paper p-[8%] [backface-visibility:hidden]'>
                            <span className='label text-[0.5rem] text-stone'>Type</span>
                            <span className='display text-[clamp(3rem,6vw,5.5rem)] leading-none text-ink wdth-112'>Aa</span>
                            <span className='label text-[0.5rem] text-stone'>Archivo, 800</span>
                        </div>

                        <div className='absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink p-[8%] text-paper [backface-visibility:hidden] [transform:rotateY(180deg)]'>
                            <HouseMark className='w-1/2' />
                            <span className='label text-[0.5rem] text-haze'>Your brochure</span>
                        </div>

                        <span data-fold-shade='' aria-hidden='true' className='pointer-events-none absolute inset-0 bg-ink opacity-0' />
                    </div>

                    <div className='relative flex flex-col justify-between border-x border-ink/10 bg-paper p-[8%]'>
                        <span className='label text-[0.5rem] text-stone'>Color</span>

                        <div className='flex flex-col gap-1.5'>
                            {SWATCHES.map(swatch => (
                                <div key={swatch.hex} className='flex items-center gap-2'>
                                    <span className={cn('size-6 shrink-0 border border-ink/10 md:size-7', swatch.className)} />
                                    <span className='label text-[0.5rem] leading-tight text-ink'>
                                        {swatch.name}
                                        <br />
                                        <span className='text-stone'>{swatch.hex}</span>
                                    </span>
                                </div>
                            ))}
                        </div>

                        <span className='label text-[0.5rem] text-stone'>Inside spread</span>
                    </div>

                    <div data-fold='right' className='relative origin-left [transform-style:preserve-3d]'>
                        <div className='absolute inset-0 flex flex-col justify-between bg-paper p-[8%] [backface-visibility:hidden]'>
                            <span className='label text-[0.5rem] text-stone'>Grid</span>

                            <span aria-hidden='true' className='grid flex-1 grid-cols-2 gap-1.5 py-3'>
                                <span className='border border-dashed border-signal/70' />
                                <span className='border border-dashed border-signal/70' />
                                <span className='col-span-2 border border-dashed border-signal/70' />
                            </span>

                            <span className='label text-[0.5rem] text-stone'>Bleed 0.125 in</span>
                        </div>

                        <div className='absolute inset-0 bg-paper [backface-visibility:hidden] [transform:rotateY(180deg)]' />
                        <span data-fold-shade='' aria-hidden='true' className='pointer-events-none absolute inset-0 bg-ink opacity-0' />
                    </div>
                </div>
            </div>

            <div aria-hidden='true' className='absolute inset-x-6 bottom-6 flex h-4 items-stretch gap-px'>
                {['bg-signal', 'bg-signal/75', 'bg-signal/50', 'bg-signal/25', 'bg-ink', 'bg-ink/60', 'bg-ink/30'].map(tint => (
                    <span key={tint} data-tint='' className={cn('flex-1 origin-bottom', tint)} />
                ))}
            </div>
        </div>
    )
}
